/**
 * Admin accounts page. Lists all non-pending users and opens a slide-panel
 * detail (AdminUserDetail) for managing each one — EIR assignment, ban/unban,
 * delete, password reset, engagement status, and secondary EIRs.
 *
 * Pending applicants are filtered out here; they are handled in
 * AdminApplications. Most actions re-fetch the affected detail (and sometimes
 * the full list) so the UI reflects server truth rather than optimistic state.
 *
 * The header also carries the platform data export — a CSV snapshot of all
 * cohort data. Unlike the other actions here, it is read-only.
 */
import React, { useEffect, useState } from 'react';
import { getAllUsers, getEIRList, getUserDetail, assignEIR, deleteUser, banUser, unbanUser, adminResetPassword, setEngagementStatus, addSecondaryEIR, removeSecondaryEIR, downloadPlatformData } from '../services/apiService';
import SlidePanel from '../components/SlidePanel';
import UserDetail, { ROLE_COLORS } from '../components/AdminUserDetail';
import AddAccountModal from '../components/AddAccountModal';
import HubSpotSyncModal from '../components/HubSpotSyncModal';

/** Admin roster of approved users with a slide-panel management detail. */
export default function AdminAccounts() {
  const [users, setUsers]         = useState([]);
  const [eirs, setEirs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHubSpot, setShowHubSpot] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  useEffect(() => {
    Promise.all([getAllUsers(), getEIRList()])
      .then(([u, e]) => { setUsers(u); setEirs(e); })
      .finally(() => setLoading(false));
  }, []);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadPlatformData();
    } catch (_) {
      setExportError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  async function openDetail(userId) {
    setSelected({ id: userId, loading: true, data: null });
    const data = await getUserDetail(userId);
    setSelected({ id: userId, loading: false, data });
  }

  if (loading) return <div style={s.loading}>Loading…</div>;

  const panelTitle = selected?.data
    ? `${selected.data.first_name} ${selected.data.last_name}`
    : 'Loading…';

  const approvedUsers = users.filter((u) => u.status !== 'pending');

  return (
    <div style={s.page} className="page-scroll">
      <div style={s.inner}>
        <div style={s.header}>
          <h1 style={s.title}>Accounts</h1>
          <div style={s.headerActions}>
            <button
              style={{ ...s.exportBtn, opacity: exporting ? 0.5 : 1 }}
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? 'Generating…' : 'Export Platform Data'}
            </button>
            <button style={s.syncBtn} onClick={() => setShowHubSpot(true)}>Sync to HubSpot</button>
            <button style={s.addBtn} onClick={() => setShowAddModal(true)}>+ Add Account</button>
          </div>
        </div>

        <p style={s.sectionNote}>
          Founders apply through the public application form. EIR, CEG, and Admin accounts are created manually.
        </p>

        {exportError && <p style={s.exportError}>{exportError}</p>}

        <div style={s.table}>
          <div style={s.tableHeader}>
            <span style={s.colName}>Name</span>
            <span style={s.colEmail}>Email</span>
            <span style={s.colRole}>Role</span>
          </div>
          {approvedUsers.length === 0 && (
            <p style={s.empty}>No accounts yet.</p>
          )}
          {approvedUsers.map((u) => {
            const color = ROLE_COLORS[u.role] || ROLE_COLORS.founder;
            return (
              <div
                key={u.id}
                style={{ ...s.tableRow, cursor: 'pointer' }}
                onClick={() => openDetail(u.id)}
              >
                <span style={s.colName}>{u.first_name} {u.last_name}</span>
                <span style={s.colEmail}>{u.email}</span>
                <span style={s.colRole}>
                  {u.engagement_status === 'active'  && <span style={s.activePill}>Active</span>}
                  {u.engagement_status === 'dormant'  && <span style={s.dormantPill}>Dormant</span>}
                  {u.engagement_status === 'archived' && <span style={s.archivedPill}>Archived</span>}
                  <span style={{ ...s.rolePill, background: color.bg, color: color.text }}>
                    {u.role.toUpperCase()}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {showHubSpot && <HubSpotSyncModal onClose={() => setShowHubSpot(false)} />}

      {showAddModal && (
        <AddAccountModal
          onClose={() => setShowAddModal(false)}
          onCreated={async () => {
            const [u, e] = await Promise.all([getAllUsers(), getEIRList()]);
            setUsers(u);
            setEirs(e);
          }}
        />
      )}

      <SlidePanel open={!!selected} onClose={() => setSelected(null)} title={panelTitle}>
        {selected?.loading && <p style={s.panelLoading}>Loading…</p>}
        {selected?.data && (
          <UserDetail
            detail={selected.data}
            eirs={eirs}
            onEIRChange={async (eirId) => {
              await assignEIR(selected.data.id, eirId);
              const updated = await getUserDetail(selected.data.id);
              setSelected((prev) => ({ ...prev, data: updated }));
              getEIRList().then(setEirs);
            }}
            onDelete={async () => {
              await deleteUser(selected.data.id);
              setSelected(null);
              const [u, e] = await Promise.all([getAllUsers(), getEIRList()]);
              setUsers(u);
              setEirs(e);
            }}
            onBan={async () => {
              await banUser(selected.data.id);
              const [updated, u] = await Promise.all([getUserDetail(selected.data.id), getAllUsers()]);
              setSelected((prev) => ({ ...prev, data: updated }));
              setUsers(u);
            }}
            onUnban={async () => {
              await unbanUser(selected.data.id);
              const [updated, u] = await Promise.all([getUserDetail(selected.data.id), getAllUsers()]);
              setSelected((prev) => ({ ...prev, data: updated }));
              setUsers(u);
            }}
            onResetPassword={async () => adminResetPassword(selected.data.id)}
            onEngagementStatusChange={async (status) => {
              await setEngagementStatus(selected.data.id, status);
              const updated = await getUserDetail(selected.data.id);
              setSelected((prev) => ({ ...prev, data: updated }));
              setUsers((prev) => prev.map((u) => u.id === selected.data.id ? { ...u, engagement_status: status } : u));
            }}
            onAddSecondaryEIR={async (eirId) => {
              await addSecondaryEIR(selected.data.id, eirId);
              const updated = await getUserDetail(selected.data.id);
              setSelected((prev) => ({ ...prev, data: updated }));
            }}
            onRemoveSecondaryEIR={async (eirId) => {
              await removeSecondaryEIR(selected.data.id, eirId);
              const updated = await getUserDetail(selected.data.id);
              setSelected((prev) => ({ ...prev, data: updated }));
            }}
          />
        )}
      </SlidePanel>
    </div>
  );
}

const s = {
  page:         { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner:        { maxWidth: 720, margin: '0 auto' },
  loading:      { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body)', color: 'var(--color-forest)' },
  header:       { marginBottom: 'var(--space-4)', paddingTop: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  addBtn:       { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--color-forest)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px 18px', cursor: 'pointer' },
  title:        { fontFamily: 'var(--font-accent)', fontSize: 36, fontWeight: 800, color: 'var(--color-rust)', lineHeight: 1.1 },
  sectionNote:  { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.65, lineHeight: 1.5, marginBottom: 'var(--space-5)' },
  headerActions:{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  syncBtn:      { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--color-forest)', background: 'var(--color-mint-dark)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px 18px', cursor: 'pointer', whiteSpace: 'nowrap' },
  exportBtn:    { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--color-forest)', background: 'var(--color-mint-dark)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px 18px', cursor: 'pointer', whiteSpace: 'nowrap' },
  exportError:  { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-rust)', marginBottom: 'var(--space-4)' },
  table:        { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', overflow: 'hidden' },
  tableHeader:  { display: 'flex', padding: '10px 20px', background: 'var(--color-mint-dark)', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: 'var(--color-forest)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  tableRow:     { display: 'flex', padding: '14px 20px', borderTop: '1px solid var(--color-mint-dark)', alignItems: 'center', gap: 8 },
  colName:      { flex: '0 0 180px', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--color-forest)' },
  colEmail:     { flex: 1, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.7 },
  colRole:      { flex: '0 0 120px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 },
  rolePill:     { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' },
  activePill:   { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 700, background: '#d1fae5', color: '#065f46', whiteSpace: 'nowrap' },
  dormantPill:  { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 700, background: '#fff8e1', color: '#7a5c00', whiteSpace: 'nowrap' },
  archivedPill: { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 700, background: '#f1f5f9', color: '#475569', whiteSpace: 'nowrap' },
  empty:        { padding: 'var(--space-6)', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.5, textAlign: 'center' },
  panelLoading: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6 },
};
